import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../utils/axios';

const API_URL = '/api/kdm';

export const fetchCertificates = createAsyncThunk(
  'kdm/fetchCertificates',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API_URL}/certificates`);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const uploadCertificate = createAsyncThunk(
  'kdm/uploadCertificate',
  async ({ name, venueName, file }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('name', name);
      formData.append('venue_name', venueName);
      formData.append('file', file);
      const { data } = await axios.post(`${API_URL}/certificates`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const deleteCertificate = createAsyncThunk(
  'kdm/deleteCertificate',
  async (certId, { rejectWithValue }) => {
    try {
      await axios.delete(`${API_URL}/certificates/${certId}`);
      return certId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const generateKDM = createAsyncThunk(
  'kdm/generate',
  async (kdmData, { rejectWithValue }) => {
    try {
      // Only send fields the API expects
      const payload = {
        dcp_job_id: kdmData.dcp_job_id,
        certificate_id: kdmData.certificate_id || undefined,
        screen_id: kdmData.screen_id || undefined,
        valid_from: kdmData.valid_from,
        valid_to: kdmData.valid_to,
        venue_name: kdmData.venue_name || undefined,
        screen_name: kdmData.screen_name || undefined,
        timezone: kdmData.timezone || undefined,
        tech_contact_email: kdmData.tech_contact_email || undefined,
        notes: kdmData.notes || undefined,
      };
      const { data } = await axios.post(`${API_URL}/generate`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const generateBulkKDM = createAsyncThunk(
  'kdm/generateBulk',
  async (bulkData, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API_URL}/generate-bulk`, bulkData, {
        headers: { 'Content-Type': 'application/json' },
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const downloadKDM = createAsyncThunk(
  'kdm/downloadKDM',
  async (recordId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/records/${recordId}/download`, {
        responseType: 'blob',
      });
      const filename = response.headers['content-disposition']
        ?.match(/filename="?([^"]+)"?/)?.[1] || `kdm-${recordId}.xml`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      return recordId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const deleteKDMRecord = createAsyncThunk(
  'kdm/deleteRecord',
  async (recordId, { rejectWithValue }) => {
    try {
      await axios.delete(`${API_URL}/records/${recordId}`);
      return recordId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const emailKDM = createAsyncThunk(
  'kdm/emailKDM',
  async (recordId, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API_URL}/records/${recordId}/email`);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const fetchKDMRecords = createAsyncThunk(
  'kdm/fetchRecords',
  async ({ dcpJobId, skip = 0, limit = 100 } = {}, { rejectWithValue }) => {
    try {
      const params = { skip, limit };
      if (dcpJobId) params.dcp_job_id = dcpJobId;
      const { data } = await axios.get(`${API_URL}/records`, { params });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

const kdmSlice = createSlice({
  name: 'kdm',
  initialState: {
    certificates: [],
    records: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearKDMError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Certificates
      .addCase(fetchCertificates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCertificates.fulfilled, (state, action) => {
        state.loading = false;
        state.certificates = action.payload;
      })
      .addCase(fetchCertificates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(uploadCertificate.fulfilled, (state, action) => {
        state.certificates.push(action.payload);
      })
      .addCase(deleteCertificate.fulfilled, (state, action) => {
        state.certificates = state.certificates.filter(c => c.id !== action.payload);
      })
      // KDM generation
      .addCase(generateKDM.fulfilled, (state, action) => {
        state.records.unshift(action.payload);
      })
      .addCase(generateBulkKDM.fulfilled, (state, action) => {
        state.records.unshift(...action.payload);
      })
      // Records
      .addCase(fetchKDMRecords.fulfilled, (state, action) => {
        state.records = action.payload;
      })
      .addCase(deleteKDMRecord.fulfilled, (state, action) => {
        state.records = state.records.filter(r => r.id !== action.payload);
      });
  },
});

export const { clearKDMError } = kdmSlice.actions;
export default kdmSlice.reducer;
